import Api from "js/core/api";
import getLogger, { LogGroup } from "js/core/logger";
import { uploadBlobToSignedUrl } from "js/core/utilities/uploadBlobToSignedUrl";
import { ShowErrorDialog, ShowMessageDialog, ShowConfirmationDialog } from "js/react/components/Dialogs/BaseDialog";
import * as gDrive from "js/core/utilities/gDrive";
import * as googleAuth from "js/core/oauth/googleAuth";
import { app } from "js/namespaces";
import { downloadFromBlob, trackActivity } from "js/core/utilities/utilities";
import PresentationEditorController from "js/editor/PresentationEditor/PresentationEditorController";
import { getSanitizedPresentationName } from "js/exporter/exportToPPT";
import ux from "js/core/services/userExperiences";

const logger = getLogger(LogGroup.EXPORT);

async function uploadExportToGoogleSlides(signedUrl, presentation, isSingleSlideExport) {
    const response = await fetch(signedUrl);
    if (!response.ok) {
        throw new Error(`Failed to download export: ${response.status}`);
    }
    const blob = await response.blob();
    const presentationFileName = getSanitizedPresentationName(presentation);

    // Google Drive API won't let us import files bigger than 100MB as Google Slides presentations
    if (blob.size > 100 * 1024 * 1024) {
        const shouldDownload = await ShowConfirmationDialog({
            title: "Presentation is too big",
            message: "Google Slides has a 100MB file size limit for imported presentations. Would you like to save the presentation locally instead?",
            okButtonLabel: "Save locally"
        });

        if (shouldDownload) {
            downloadFromBlob(blob, `${presentationFileName}.pptx`, "application/vnd.openxmlformats-officedocument.presentationml.presentation");
        }

        return;
    }

    const { writeUrl, fileName: tempAssetFileName } = await Api.tempAssets.post();
    await uploadBlobToSignedUrl(blob, writeUrl, "application/vnd.openxmlformats-officedocument.presentationml.presentation");

    const { fileId } = await Api.gDriveGoogleSlides.post({ presentationFileName, tempAssetFileName });

    const openGoogleSlides = () => window.open(`https://docs.google.com/presentation/d/${fileId}`, "_blank");

    const newWindow = openGoogleSlides();
    if (!newWindow) {
        // New window was blocked, show dialog
        ShowMessageDialog({
            title: "Success",
            message: "Your presentation was successfully exported to Google Slides",
            buttonText: "Open Google Slides",
            onClick: openGoogleSlides,
            cancelButtonLabel: "Dismiss",
            preventClose: false,
            showCancel: true,
            buttonOptions: { color: "blue" }
        });
    }

    if (isSingleSlideExport) {
        trackActivity("Presentation", "SlideExport", null, null, { type: "google" }, { audit: true });
    } else {
        trackActivity("Presentation", "GoogleExport", null, null, {}, { audit: true });
    }
}

export async function exportToGoogleSlides({ includeSkippedSlides = false, slide = null } = {}) {
    const presentation = PresentationEditorController.presentation;
    const slideIds = slide?.id ? [slide.id] : undefined;
    const isSingleSlideExport = Boolean(slideIds);

    ux.exportPresentation.register({ presentationId: presentation.id, subtype: ux.Subtype[ux.Type.EXPORT_PRESENTATION].EXPORT_TO_GOOGLE_SLIDES });

    try {
        const { hasOfflineAccess } = await gDrive.getState();
        if (!hasOfflineAccess) {
            await gDrive.grantOfflineAccessWithDialogIfNeeded(app.user.id);
        }

        presentation.enqueueExportCache("pptx", async signedUrl => {
            try {
                await uploadExportToGoogleSlides(signedUrl, presentation, isSingleSlideExport);
                ux.exportPresentation.complete(undefined, ({ presentationId }) => presentationId === presentation.id);
            } catch (err) {
                if (err instanceof googleAuth.GoogleAuthFlowWasInterruptedError) {
                    ux.exportPresentation.abandon(undefined, ({ presentationId }) => presentationId === presentation.id);
                    return;
                }

                logger.error(err, "exportToGoogleSlides() failed");
                ux.exportPresentation.fail({ error: err }, ({ presentationId }) => presentationId === presentation.id);

                if (err.message === "Wrong user") {
                    ShowErrorDialog({
                        title: "Wrong user",
                        message: "Looks like you logged into Google as a wrong user"
                    });
                    return;
                }

                ShowErrorDialog({
                    title: "Failed to export presentation",
                    message: "We couldn't export your presentation, please try again later"
                });
            }
        }, {
            title: `Exporting ${isSingleSlideExport ? "slide" : "presentation"} to Google Slides...`,
            includeSkippedSlides,
            pptxMode: "editable",
            slideIds,
        });
    } catch (err) {
        if (err instanceof googleAuth.GoogleAuthFlowWasInterruptedError) {
            ux.exportPresentation.abandon(undefined, ({ presentationId }) => presentationId === presentation.id);
            return;
        }

        logger.error(err, "exportToGoogleSlides() failed");
        ux.exportPresentation.fail({ error: err }, ({ presentationId }) => presentationId === presentation.id);

        if (err.message === "Wrong user") {
            ShowErrorDialog({
                title: "Wrong user",
                message: "Looks like you logged into Google as a wrong user"
            });
            return;
        }

        ShowErrorDialog({
            title: "Failed to export presentation",
            message: "We couldn't export your presentation, please try again later"
        });
    }
}
